import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { APPT_TYPES, itDate } from "../lib/constants";
import AttachmentsPanel from "./AttachmentsPanel";
import ClientSelect from "./ClientSelect";
import CategorySelect from "./CategorySelect";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { MapPin, Clock, Users, Phone, CheckCircle2, MessageCircle, Pencil, Trash2 } from "lucide-react";

const APPT_STATUSES = ["programmato", "confermato", "in_corso", "completato", "annullato"];

export default function AppointmentDialog({ appointmentId, open, onClose, onChanged, categories = [] }) {
  const { can } = useAuth();
  const canEdit = can("edit_calendar");
  const [appt, setAppt] = useState(null);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({});
  const [clients, setClients] = useState([]);
  const [teams, setTeams] = useState([]);
  const [collabs, setCollabs] = useState([]);

  const load = async () => {
    if (!appointmentId) return;
    const { data } = await api.get(`/appointments/${appointmentId}`);
    setAppt(data);
  };
  useEffect(() => { if (open) { setEdit(false); load(); } /* eslint-disable-next-line */ }, [appointmentId, open]);
  useEffect(() => {
    if (edit && canEdit && clients.length === 0) {
      api.get("/clients").then((r) => setClients(r.data));
      api.get("/teams").then((r) => setTeams(r.data));
      api.get("/collaborators").then((r) => setCollabs(r.data));
    }
  }, [edit, canEdit, clients.length]);

  if (!appt) return <Dialog open={open} onOpenChange={onClose}><DialogContent>Caricamento…</DialogContent></Dialog>;

  const type = APPT_TYPES.find((t) => t.value === appt.type);

  const setStatus = async (status) => {
    await api.put(`/appointments/${appt.id}`, { status });
    toast.success("Stato aggiornato");
    load(); onChanged && onChanged();
    if (appt.job_id && status === "completato") {
      try { await api.put(`/jobs/${appt.job_id}`, { status: "completato" }); } catch {}
    }
  };

  const openEdit = () => {
    setForm({ type: appt.type, client_id: appt.client_id, title: appt.title, date: appt.date,
      start_time: appt.start_time, end_time: appt.end_time, address: appt.address, locality: appt.locality,
      team_id: appt.team_id, collaborator_ids: appt.collaborator_ids || [], operational_notes: appt.operational_notes, category_id: appt.category_id || "" });
    setEdit(true);
  };
  const saveEdit = async () => {
    if (!form.date || !form.start_time) return toast.error("Data e ora obbligatorie");
    try {
      await api.put(`/appointments/${appt.id}`, { ...form, collaborator_ids: form.collaborator_ids || [] });
      toast.success("Appuntamento aggiornato"); setEdit(false); load(); onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Salvataggio non riuscito. I dati inseriti sono stati mantenuti."); }
  };
  const remove = async () => {
    if (!window.confirm("Annullare questo appuntamento?\n\nResterà consultabile nello storico come 'annullato' e non apparirà più tra i prossimi appuntamenti.")) return;
    try { await api.delete(`/appointments/${appt.id}`); toast.success("Appuntamento annullato"); onChanged && onChanged(); onClose && onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Operazione non riuscita"); }
  };

  const prepareClientWa = async () => {
    try {
      const { data } = await api.get("/whatsapp/config");
      const tpl = data.client_template || "";
      const intervento = appt.title || "intervento";
      const msg = tpl.replace("{nome}", appt.client?.name || "").replace("{intervento}", intervento)
        .replace("{data}", itDate(appt.date)).replace("{ora}", appt.start_time || "")
        .replace("{indirizzo}", appt.address || "").replace("{numero_aziendale}", data.company_phone || "");
      const phone = (appt.client?.phone || "").replace(/\D/g, "");
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, "_blank");
    } catch { toast.error("Impossibile preparare il messaggio"); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="appointment-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="px-2.5 py-1 rounded-full text-xs font-semibold text-white" style={{ background: type?.color }}>{type?.label}</span>
            <DialogTitle className="font-head">{appt.client_name || appt.title}</DialogTitle>
            {appt.status && <span className="text-xs px-2 py-0.5 rounded-full bg-accent capitalize">{appt.status.replace("_", " ")}</span>}
          </div>
        </DialogHeader>

        {!edit && (
          <>
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2"><Clock size={16} className="text-brand" /> {itDate(appt.date)} · {appt.start_time}{appt.end_time ? `–${appt.end_time}` : ""}</div>
              {appt.address && <div className="flex items-center gap-2"><MapPin size={16} className="text-brand" /> {appt.address}{appt.locality ? `, ${appt.locality}` : ""}</div>}
              {appt.client?.phone && <a href={`tel:${appt.client.phone}`} className="flex items-center gap-2 text-brand"><Phone size={16} /> {appt.client.phone}</a>}
              {appt.team_name && <div className="flex items-center gap-2"><Users size={16} className="text-brand" /> {appt.team_name}</div>}
            </div>
            {appt.operational_notes && <div className="bg-accent rounded-lg p-3 text-sm"><span className="font-medium">Note operative:</span> {appt.operational_notes}</div>}
            {appt.address && (
              <a href={`https://maps.google.com/?q=${encodeURIComponent((appt.address || "") + " " + (appt.locality || ""))}`} target="_blank" rel="noreferrer">
                <Button variant="outline" size="sm" className="w-full"><MapPin size={15} className="mr-1" /> Apri in mappa</Button>
              </a>
            )}

            {canEdit && (
              <div className="flex flex-wrap gap-1.5 items-center">
                <span className="text-xs text-muted-foreground mr-1">Stato:</span>
                {APPT_STATUSES.map((s) => (
                  <button key={s} data-testid={`appt-status-${s}`} onClick={() => setStatus(s)}
                    className={`text-xs px-2.5 py-1 rounded-full border capitalize transition ${appt.status === s ? "bg-brand text-white border-brand" : "bg-white border-border text-muted-foreground"}`}>
                    {s.replace("_", " ")}
                  </button>
                ))}
              </div>
            )}

            {canEdit && appt.status !== "completato" && (
              <Button data-testid="btn-mark-done" onClick={() => setStatus("completato")} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <CheckCircle2 size={16} className="mr-1" /> {appt.type === "montaggio" ? "Segna lavoro come completato" : "Segna sopralluogo come svolto"}
              </Button>
            )}

            {canEdit && (
              <div className="flex gap-2">
                <Button variant="outline" data-testid="btn-edit-appt" onClick={openEdit} className="flex-1"><Pencil size={15} className="mr-1" /> Modifica</Button>
                <Button variant="outline" data-testid="btn-delete-appt" onClick={remove} className="flex-1 text-destructive border-destructive/40 hover:bg-red-50"><Trash2 size={15} className="mr-1" /> Annulla appuntamento</Button>
              </div>
            )}
          </>
        )}

        {edit && canEdit && (
          <div className="space-y-3">
            <div><Label>Tipo</Label>
              <Select value={form.type || "sopralluogo"} onValueChange={(v)=>setForm({...form,type:v})}>
                <SelectTrigger data-testid="edit-appt-type"><SelectValue /></SelectTrigger>
                <SelectContent onCloseAutoFocus={(e) => e.preventDefault()}>{APPT_TYPES.map((t)=><SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Categoria (colore)</Label><CategorySelect testid="edit-appt-category" value={form.category_id} categories={categories} onChange={(v) => setForm({ ...form, category_id: v })} /></div>
            <div><Label>Cliente</Label>
              <ClientSelect testid="edit-appt-client" value={form.client_id} clients={clients} onCreated={(c) => setClients((l) => [...l, c])}
                onPick={(c) => setForm((f) => ({ ...f, client_id: c?.id, address: f.address || c?.address, locality: f.locality || c?.locality }))} />
            </div>
            <div><Label>Titolo / descrizione</Label><Input data-testid="edit-appt-title" value={form.title||""} onChange={(e)=>setForm({...form,title:e.target.value})} /></div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label>Data</Label><Input type="date" data-testid="edit-appt-date" value={form.date||""} onChange={(e)=>setForm({...form,date:e.target.value})} /></div>
              <div><Label>Inizio</Label><Input type="time" data-testid="edit-appt-start" value={form.start_time||""} onChange={(e)=>setForm({...form,start_time:e.target.value})} /></div>
              <div><Label>Fine</Label><Input type="time" value={form.end_time||""} onChange={(e)=>setForm({...form,end_time:e.target.value})} /></div>
            </div>
            <div><Label>Indirizzo</Label><Input value={form.address||""} onChange={(e)=>setForm({...form,address:e.target.value})} /></div>
            <div><Label>Squadra</Label>
              <Select value={form.team_id || ""} onValueChange={(v)=>setForm({...form,team_id:v})}>
                <SelectTrigger data-testid="edit-appt-team"><SelectValue placeholder="Nessuna" /></SelectTrigger>
                <SelectContent onCloseAutoFocus={(e) => e.preventDefault()}>{teams.map((t)=><SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Collaboratori</Label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {collabs.map((c)=>{const sel=(form.collaborator_ids||[]).includes(c.id);return(
                  <button key={c.id} type="button" onClick={()=>{const cur=form.collaborator_ids||[];setForm({...form,collaborator_ids:sel?cur.filter(x=>x!==c.id):[...cur,c.id]});}}
                    className={`text-xs px-2.5 py-1 rounded-full border ${sel?"bg-brand text-white border-brand":"bg-white text-muted-foreground"}`}>{c.name}</button>);})}
              </div>
            </div>
            <div><Label>Note operative</Label><Textarea value={form.operational_notes||""} onChange={(e)=>setForm({...form,operational_notes:e.target.value})} /></div>
            <DialogFooter><Button variant="outline" onClick={()=>setEdit(false)}>Annulla</Button><Button className="bg-brand text-white" data-testid="edit-appt-save" onClick={saveEdit}>Salva modifiche</Button></DialogFooter>
          </div>
        )}

        {!edit && (
          <div className="border-t border-border pt-4">
            <AttachmentsPanel appointmentId={appt.id} clientId={appt.client_id} jobId={appt.job_id} quoteId={appt.quote_id}
              title={appt.type === "montaggio" ? "Foto lavoro e foglio fine lavori" : "Foto e allegati"} />
            <p className="text-xs text-muted-foreground mt-2">Le foto caricate qui compaiono automaticamente anche negli Allegati del cliente.</p>
          </div>
        )}

        {!edit && can("view_clients") && appt.client?.phone && (
          <Button variant="outline" onClick={prepareClientWa} data-testid="btn-prepare-wa-client" className="w-full">
            <MessageCircle size={16} className="mr-1" /> Prepara WhatsApp al cliente
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}
